import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { FaCalendarAlt, FaClock, FaRegClock, FaMapMarkedAlt, FaMapMarked, FaServer, FaMap, FaRoad, FaUsers, FaStar, FaLink, FaPaperPlane, FaSearch, FaGamepad } from 'react-icons/fa';

const API_BASE = 'https://hub.mileageriot.com';

const ConvoyInvites = () => {
  const [preview, setPreview] = useState(null);
  const [tmpUrl, setTmpUrl] = useState('');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [discord, setDiscord] = useState('');
  const [distance, setDistance] = useState('');
  const [bookingLink, setBookingLink] = useState('');
  const [slotLink, setSlotLink] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [status, setStatus] = useState(null); // {type: 'error'|'success', text: '...'}
  const { t } = useTranslation();
  const ref = useRef();

  useEffect(() => {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add('animate-in'); });
    }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const fmtDate = (s) => s ? new Intl.DateTimeFormat('ru', { year:'numeric', month:'2-digit', day:'2-digit' }).format(new Date(s)) : '-';
  const fmtTime = (s) => s ? new Intl.DateTimeFormat('ru', { hour:'2-digit', minute:'2-digit' }).format(new Date(s)) : '-';

  const handlePreview = async () => {
    if (!tmpUrl.trim()) return;
    setLoading(true); setStatus(null); setPreview(null);
    try {
      const res = await fetch(`${API_BASE}/api/convoy-invites/preview`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: tmpUrl })
      });
      const data = await res.json();
      if (!data.success) setStatus({ type: 'error', text: data.message });
      else setPreview(data.data);
    } catch { setStatus({ type: 'error', text: t('inviteConvoy.fetchError') }); }
    setLoading(false);
  };

  const handleSubmit = async () => {
    if (!tmpUrl.trim() || !name.trim()) return;
    setSubmitLoading(true); setStatus(null);
    try {
      const res = await fetch(`${API_BASE}/api/convoy-invites/store`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: tmpUrl, name, email: email || null, discord: discord || null, distance: distance || null, booking_link: bookingLink || null, slot_link: slotLink || null, description: description || null })
      });
      const data = await res.json();
      if (!data.success) setStatus({ type: 'error', text: data.message });
      else { setStatus({ type: 'success', text: t('inviteConvoy.submitted') }); setPreview(null); setTmpUrl(''); setName(''); setEmail(''); setDiscord(''); setDistance(''); setBookingLink(''); setSlotLink(''); setDescription(''); }
    } catch { setStatus({ type: 'error', text: t('inviteConvoy.fetchError') }); }
    setSubmitLoading(false);
  };

  const d = preview; // shorthand

  return (
    <>
      <Helmet>
        <title>{t('inviteConvoy.pageTitle')} — Mileage Riot</title>
        <meta name="description" content={t('inviteConvoy.pageDesc')} />
        <link rel="canonical" href="https://mileageriot.com/invite-convoy" />
      </Helmet>

      <section className="events-modern page-events" ref={ref}>
        <div className="container">

          {/* Header — identical to Events */}
          <div className="section-header text-center mb-5 fade-in-up stagger-1">
            <div className="section-badge">
              <FaStar className="me-2" />
              <span>{t('inviteConvoy.badge')}</span>
            </div>
            <h2 className="section-title text-glow text-white">
              {t('inviteConvoy.title')}
            </h2>
            <div className="section-decoration mx-auto"></div>
            <p className="text-white-50 mt-3 section-subtitle">
              {t('inviteConvoy.subtitle')}
            </p>
          </div>

          {/* Link Input */}
          <div className="row justify-content-center mb-5 fade-in-up stagger-2">
            <div className="col-md-8 col-12">
              <div className="card glass-container event-card-modern" style={{ minHeight: 'auto' }}>
                <div className="p-4">
                  <h5 className="text-white event-title" style={{ minHeight: 'auto', marginBottom: '1rem' }}>
                    <FaLink className="me-2 text-primary" />
                    {t('inviteConvoy.paste_tmp_link')}
                  </h5>
                  <div className="event-divider"></div>

                  <div className="d-flex gap-2 mt-3">
                    <input
                      type="text"
                      className="flex-grow-1"
                      placeholder="https://truckersmp.com/events/12345"
                      value={tmpUrl}
                      onChange={(e) => setTmpUrl(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && handlePreview()}
                      style={{
                        background: 'rgba(255,255,255,0.03)',
                        border: '1px solid rgba(0,203,205,0.2)',
                        color: '#fff',
                        borderRadius: '20px',
                        padding: '12px 20px',
                        fontSize: '0.95rem',
                        outline: 'none',
                        width: '100%',
                      }}
                    />
                    <button
                      className="btn btn-hero-primary btn-sm pulse-glow"
                      onClick={handlePreview}
                      disabled={loading || !tmpUrl.trim()}
                      style={{ whiteSpace: 'nowrap', padding: '12px 24px' }}
                    >
                      {loading ? <span className="spinner-border spinner-border-sm" /> : <><FaSearch className="me-2" />{t('inviteConvoy.preview')}</>}
                    </button>
                  </div>

                  {status && (
                    <div className="mt-3" style={{
                      padding: '10px 16px',
                      borderRadius: '12px',
                      background: status.type === 'error' ? 'rgba(234,84,85,0.1)' : 'rgba(40,199,111,0.1)',
                      border: `1px solid ${status.type === 'error' ? 'rgba(234,84,85,0.3)' : 'rgba(40,199,111,0.3)'}`,
                      color: status.type === 'error' ? '#ea5455' : '#28c76f',
                      fontSize: '0.9rem'
                    }}>
                      {status.text}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Preview Card — same structure as event card */}
          {d && (
            <div className="row mt-5 fade-in-up stagger-2">
              <div className="col-md-8 mb-4 col-12 mx-auto">
                <div className="card glass-container event-card-modern" style={{ minHeight: 'auto' }}>
                  <div className="event-image-container">
                    <img loading="lazy" decoding="async"
                      className="img-fluid rounded event-image"
                      src={d.banner || 'https://hub.mileageriot.com/assets/images/banner/banner.jpg'}
                      alt={d.name}
                      onError={(e) => { e.target.onerror = null; e.target.src = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg'; }}
                    />
                    <div className="event-overlay">
                      <div className="event-date-badge">
                        <FaCalendarAlt className="me-2" />
                        {fmtDate(d.start_at)}
                      </div>
                    </div>
                  </div>

                  <div className="p-4">
                    <h5 className="text-white event-title">{d.name}</h5>
                    <div className="event-divider"></div>

                    <div className="event-details">
                      <div className="event-detail-item">
                        <FaClock className="text-primary me-2" />
                        <span className="detail-label">{t('inviteConvoy.meetup')}:</span>
                        <span className="text-primary detail-value">{fmtTime(d.meetup_at)} UTC</span>
                      </div>
                      <div className="event-detail-item">
                        <FaRegClock className="text-primary me-2" />
                        <span className="detail-label">{t('inviteConvoy.departure')}:</span>
                        <span className="text-primary detail-value">{fmtTime(d.start_at)} UTC</span>
                      </div>
                      <div className="event-detail-item">
                        <FaMapMarkedAlt className="text-primary me-2" />
                        <span className="detail-label">{t('inviteConvoy.from')}:</span>
                        <span className="text-primary detail-value">{d.departure?.city} ({d.departure?.location})</span>
                      </div>
                      <div className="event-detail-item">
                        <FaMapMarked className="text-primary me-2" />
                        <span className="detail-label">{t('inviteConvoy.to')}:</span>
                        <span className="text-primary detail-value">{d.arrive?.city} ({d.arrive?.location})</span>
                      </div>
                      {d.dlcs && Object.keys(d.dlcs).length > 0 && (
                        <div className="event-detail-item">
                          <FaMap className="text-primary me-2" />
                          <span className="detail-label">DLC:</span>
                          <span className="text-primary detail-value">{Object.values(d.dlcs).join(', ')}</span>
                        </div>
                      )}
                      <div className="event-detail-item">
                        <FaServer className="text-primary me-2" />
                        <span className="detail-label">{t('inviteConvoy.server')}:</span>
                        <span className="text-primary detail-value">{d.server?.name}</span>
                      </div>
                      <div className="event-detail-item">
                        <FaGamepad className="text-primary me-2" />
                        <span className="detail-label">Game:</span>
                        <span className="text-primary detail-value">{d.game}</span>
                      </div>
                      <div className="event-detail-item">
                        <FaUsers className="text-primary me-2" />
                        <span className="detail-label">{t('inviteConvoy.confirmed')}:</span>
                        <span className="text-primary detail-value">{d.attendances?.confirmed_users?.length || 0}</span>
                      </div>
                    </div>

                    {/* Submit form */}
                    <div className="event-divider mt-4"></div>
                    <h5 className="text-white mt-3 mb-3" style={{ fontWeight: 700, fontSize: '1.1rem' }}>
                      <FaPaperPlane className="me-2 text-primary" />
                      {t('inviteConvoy.submitForm')}
                    </h5>
                    <div className="row g-2">
                      <div className="col-md-4">
                        <input type="text" placeholder={t('inviteConvoy.yourName') + ' *'} value={name} onChange={(e) => setName(e.target.value)}
                          style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(0,203,205,0.15)', color:'#fff', borderRadius:'12px', padding:'10px 16px', fontSize:'0.9rem', outline:'none' }} />
                      </div>
                      <div className="col-md-4">
                        <input type="email" placeholder={t('inviteConvoy.yourEmail') + ' *'} value={email} onChange={(e) => setEmail(e.target.value)}
                          style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(0,203,205,0.15)', color:'#fff', borderRadius:'12px', padding:'10px 16px', fontSize:'0.9rem', outline:'none' }} />
                      </div>
                      <div className="col-md-4">
                        <input type="text" placeholder="Discord" value={discord} onChange={(e) => setDiscord(e.target.value)}
                          style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', color:'#fff', borderRadius:'12px', padding:'10px 16px', fontSize:'0.9rem', outline:'none' }} />
                      </div>
                      <div className="col-md-6">
                        <input type="number" placeholder={t('inviteConvoy.distance') + ' *'} value={distance} onChange={(e) => setDistance(e.target.value)}
                          style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(0,203,205,0.15)', color:'#fff', borderRadius:'12px', padding:'10px 16px', fontSize:'0.9rem', outline:'none' }} />
                      </div>
                      <div className="col-md-6">
                        <input type="url" placeholder={t('inviteConvoy.bookingLink')} value={bookingLink} onChange={(e) => setBookingLink(e.target.value)}
                          style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', color:'#fff', borderRadius:'12px', padding:'10px 16px', fontSize:'0.9rem', outline:'none' }} />
                      </div>
                      <div className="col-12">
                        <input type="url" placeholder={t('inviteConvoy.slotLink')} value={slotLink} onChange={(e) => setSlotLink(e.target.value)}
                          style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', color:'#fff', borderRadius:'12px', padding:'10px 16px', fontSize:'0.9rem', outline:'none' }} />
                      </div>
                      <div className="col-12">
                        <textarea placeholder={t('inviteConvoy.description')} value={description} onChange={(e) => setDescription(e.target.value)} rows={3}
                          style={{ width:'100%', background:'rgba(255,255,255,0.03)', border:'1px solid rgba(255,255,255,0.08)', color:'#fff', borderRadius:'12px', padding:'10px 16px', fontSize:'0.9rem', outline:'none', resize:'vertical' }} />
                      </div>
                      <div className="col-12 mt-2">
                        <button
                          className="btn btn-hero-primary btn-sm w-100 pulse-glow"
                          onClick={handleSubmit}
                          disabled={submitLoading || !name.trim() || !email.trim() || !distance}
                          style={{ padding: '12px 16px' }}
                        >
                          {submitLoading ? <span className="spinner-border spinner-border-sm" /> : <><FaPaperPlane className="me-2" />{t('inviteConvoy.submit')}</>}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Background decoration — same as Events */}
        <div className="events-bg-decoration"></div>
      </section>
    </>
  );
};

export default ConvoyInvites;
